import React from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, MessageCircle } from 'lucide-react';
import ContactForm from '../components/ContactForm';
import { GlassCard } from '../components/GlassCard';

const Contact = ({ lang = 'en' }) => {
  const isAr = lang === 'ar';

  const contactInfo = [
    {
      icon: <Phone className='text-medical-blue-400' size={24} />,
      title: isAr ? 'اتصل بنا' : 'Call Us',
      detail: '+965 50097296',
      link: 'tel:+96550097296',
    },
    {
      icon: <MessageCircle className='text-green-400' size={24} />,
      title: 'WhatsApp',
      detail: '+965 50097296',
      link: 'https://wa.me/96550097296',
    },
    {
      icon: <Mail className='text-medical-blue-400' size={24} />,
      title: isAr ? 'البريد الإلكتروني' : 'Email Us',
      detail: 'info@rightchoicekw.com',
      link: 'mailto:info@rightchoicekw.com',
    },
    {
      icon: <MapPin className='text-trust-teal' size={24} />,
      title: isAr ? 'موقعنا' : 'Our Location',
      detail: isAr
        ? 'مدينة الكويت، الكويحولي، بلوك 7، شارع تونس، مجمع الرحاب'
        : 'Hawally, block 7, Tunisia street, Al rehab complex',
      link: '#',
    },
  ];

  return (
    <div
      className='pt-24 pb-20 bg-[#050505]' // Changed to Deep Black
      dir={isAr ? 'rtl' : 'ltr'}
    >
      {/* Header Section */}
      <section className='section-padding py-16 text-center'>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className='text-4xl md:text-6xl font-black text-white mb-6'>
            {isAr ? 'تواصل معنا' : 'Get in Touch'}
          </h1>
          <p className='text-lg text-gray-400 max-w-2xl mx-auto font-medium'>
            {isAr
              ? 'نحن هنا لتقديم الدعم والرعاية التي تستحقها عائلتكم. لا تترددوا في الاتصال بنا في أي وقت.'
              : 'We are here to provide the support and care your family deserves. Feel free to reach out to us anytime.'}
          </p>
        </motion.div>
      </section>

      <section className='section-padding max-w-7xl mx-auto'>
        <div className='grid lg:grid-cols-3 gap-12'>
          {/* Contact Details Column */}
          <div className='lg:col-span-1 space-y-6'>
            <h2
              className={`text-2xl font-black text-white mb-8 ${isAr ? 'text-right' : 'text-left'}`}
            >
              {isAr ? 'معلومات الاتصال' : 'Contact Information'}
            </h2>

            <div className='grid gap-4'>
              {contactInfo.map((item, index) => (
                <motion.a
                  key={index}
                  href={item.link}
                  initial={{ opacity: 0, x: isAr ? 20 : -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className='flex items-center gap-5 p-6 bg-white/[0.03] rounded-3xl border border-white/5 hover:border-medical-blue-500/50 hover:bg-white/[0.05] transition-all group'
                >
                  <div className='p-3 bg-white/5 border border-white/10 rounded-2xl group-hover:bg-medical-blue-600 group-hover:text-white transition-colors'>
                    {item.icon}
                  </div>
                  <div className={isAr ? 'text-right' : 'text-left'}>
                    <p className='text-xs font-bold text-gray-500 uppercase tracking-widest mb-1'>
                      {item.title}
                    </p>
                    <p className='text-lg font-black text-white'>
                      {item.detail}
                    </p>
                  </div>
                </motion.a>
              ))}
            </div>

            {/* Availability Card */}
            <GlassCard className='p-8 border-trust-teal/30 bg-trust-teal/5 mt-8'>
              <div className='flex items-center gap-4 mb-4'>
                <div className='p-2 bg-trust-teal/20 rounded-lg'>
                  <Clock className='text-trust-teal' size={28} />
                </div>
                <h3 className='text-xl font-black text-white'>
                  {isAr ? 'ساعات العمل' : 'Working Hours'}
                </h3>
              </div>
              <p className='text-sm font-bold text-gray-400 leading-relaxed'>
                {isAr
                  ? 'خدمات التمريض والرعاية لدينا متوفرة على مدار ٢٤ ساعة طوال أيام الأسبوع.'
                  : 'Our nursing and care services are available 24 hours a day, 7 days a week.'}
              </p>
            </GlassCard>
          </div>

          {/* Form Column */}
          <div className='lg:col-span-2'>
            <ContactForm lang={lang} isDark={true} />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
